import React, { Component } from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";

import EqualizerOutlinedIcon from "@mui/icons-material/EqualizerOutlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";

//import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import BalanceOutlinedIcon from "@mui/icons-material/BalanceOutlined";
import TrendingDownOutlinedIcon from "@mui/icons-material/TrendingDownOutlined";
import TrendingUpOutlinedIcon from "@mui/icons-material/TrendingUpOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import FindInPageOutlinedIcon from "@mui/icons-material/FindInPageOutlined";

//import TrackChangesOutlinedIcon from "@mui/icons-material/TrackChangesOutlined";
import CheckCircleOutlineOutlinedIcon from "@mui/icons-material/CheckCircleOutlineOutlined";
//import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";

import DashboardHeader from "./DashboardHeader";
import SummaryCard from "../../components/dashlibs/SummaryCard";
import SectionCard from "../../components/dashlibs/SectionCard";
import StatusBadge from "../../components/dashlibs/StatusBadge";
import AlertList from "../../components/dashlibs/AlertList";
import { MockBarChart, MockTrendChart } from "../../components/dashlibs/MockChart";
import { DashboardPageProps } from "./dashboard.types";

import type { ChartTone } from "../../components/charts";
import { DonutChart, AreaTrendChart, BarChart, TreemapChart } from "../../components/charts";

const varianceTrend = [12, 18, 15, 22, 19, 28, 31, 26, 24, 29, 34, 30];

const topNegativeVariance = [
  { label: "Achats hydrocarbures", value: 82, color: "#C0392B" },
  { label: "Sécurisation dépôts", value: 68, color: "#D68910" },
  { label: "Réplication infra", value: 61, color: "#A93226" },
  { label: "Maintenance Bingo", value: 54, color: "#CB4335" },
];

const topPositiveVariance = [
  { label: "Charges fonctionnement", value: 41, color: "#116B43" },
  { label: "Formations", value: 35, color: "#2471A3" },
  { label: "Appui gouvernance", value: 29, color: "#163A5F" },
  { label: "Communication", value: 22, color: "#2E86C1" },
];

const rootCauseMix = [
  { label: "Retard procurement", value: 46, color: "#D68910" },
  { label: "Sous-planification", value: 38, color: "#C0392B" },
  { label: "Glissement activité", value: 52, color: "#2471A3" },
  { label: "Écart prix/unité", value: 61, color: "#7D3C98" },
];

const riskAlerts = [
  {
    title: "Variance négative sur achats hydrocarbures",
    description: "Le niveau de consommation dépasse la trajectoire projetée et nécessite un arbitrage rapide.",
    tone: "danger" as const,
  },
  {
    title: "Sous-exécution sur investissements courants",
    description: "Plusieurs lignes présentent un décalage important entre budget disponible et exécution réelle.",
    tone: "warning" as const,
  },
  {
    title: "Variances positives sur fonctionnement",
    description: "Certaines masses restent sous l’enveloppe projetée sans risque immédiat.",
    tone: "success" as const,
  },
];

const lineItemsAtRisk = [
  { label: "LI-SUP-BIN-25", value: 78, color: "#C0392B" },
  { label: "LI-GAS-BOB-25", value: 66, color: "#D68910" },
  { label: "LI-INV-BIN-SEC", value: 58, color: "#A93226" },
  { label: "LI-REP-INFRA", value: 64, color: "#7D3C98" },
];

export default class VarianceDashboard extends Component<DashboardPageProps,any> {

  render(){
  const {userData,dashData,sharedFilters,filterOptions,onFilterChange,onRefresh} = this.props;
  const {meta,sections={},summaryCards=[]} = dashData || {};

  const { varianceByAccount={},varianceByCostCenter={} } = sections;
  const { overrunUnderrun={}, criticalVarianceAlerts={} } = sections;

  const iconMap: any = {    
    insights: <InsightsOutlinedIcon />,
    equalizer: <EqualizerOutlinedIcon />,
    warning: <WarningAmberOutlinedIcon />,
    check: <CheckCircleOutlineOutlinedIcon />,
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, display: "grid", gap: 2.5 }}>
      <DashboardHeader
        title="Dashboard Variance & Risques"
        subtitle="Vue analytique des écarts budgétaires, des risques de dérive, des causes probables et des lignes sous tension."
        userData={userData}
        sharedFilters={sharedFilters}
        filterOptions={filterOptions}
        onFilterChange={onFilterChange}
        onRefresh={onRefresh}
      />

      <Box sx={{ display: "grid", gridTemplateColumns: {xs: "1fr",sm: "repeat(2, 1fr)",lg: "repeat(4, 1fr)",}, gap: 2 }}>
        {summaryCards.map((card: any) => (
          <SummaryCard
            key={card.code}
            title={card.label}
            value={card.valueFormatted}
            subtitle={card.subtitle}
            delta={card.deltaFormatted}
            tone={card.tone}
            icon={iconMap[card.icon] || <TrackChangesOutlinedIcon />}
          />
        ))}
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
        <SectionCard
          title={varianceByAccount.title || ""}
          subtitle={varianceByAccount.subtitle || ""}
        >
          <BarChart data={(varianceByAccount.data || [])} />
        </SectionCard>

         <SectionCard
          title={criticalVarianceAlerts.title || ""}
          subtitle={criticalVarianceAlerts.subtitle || ""}
        >
          <AlertList items={criticalVarianceAlerts.data || []} />
        </SectionCard>
      </Box>

      <Box sx={{ display: "grid", gap: 2, gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" } }}>
        <SectionCard
          title={varianceByCostCenter.title || ""}
          subtitle={varianceByCostCenter.subtitle || ""}
        >
          <TreemapChart data={varianceByCostCenter.data || []} />
        </SectionCard>
        
        <SectionCard
          title={overrunUnderrun.title || ""}
          subtitle={overrunUnderrun.subtitle || ""}
        >
          <DonutChart data={(overrunUnderrun.data || [])} />
        </SectionCard>
      </Box>

      {/* <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: {
            xs: "1fr",
            sm: "repeat(2, 1fr)",
            lg: "repeat(6, 1fr)",
          },
        }}
      >
        <SummaryCard
          title="Variances critiques"
          value="11"
          subtitle="Cas majeurs détectés"
          delta="4 hautes"
          tone="danger"
          icon={<WarningAmberOutlinedIcon />}
        />
        <SummaryCard
          title="Écart cumulé"
          value="-6,4 Md"
          subtitle="Budget vs projection"
          delta="-9,1%"
          tone="warning"
          icon={<BalanceOutlinedIcon />}
        />
        <SummaryCard
          title="Écart négatif"
          value="7"
          subtitle="Au-dessus du budget"
          delta="À corriger"
          tone="danger"
          icon={<TrendingDownOutlinedIcon />}
        />
        <SummaryCard
          title="Écart positif"
          value="4"
          subtitle="Sous-consommation"
          delta="Opportunités"
          tone="success"
          icon={<TrendingUpOutlinedIcon />}
        />
        <SummaryCard
          title="Lignes à risque"
          value="9"
          subtitle="Sous surveillance"
          delta="Top 5 prioritaires"
          tone="warning"
          icon={<ErrorOutlineOutlinedIcon />}
        />
        <SummaryCard
          title="Causes tracées"
          value="83%"
          subtitle="Anomalies qualifiées"
          delta="+6 pts"
          tone="info"
          icon={<FindInPageOutlinedIcon />}
        />
      </Box>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <SectionCard
            title="Tendance des variances"
            subtitle="Évolution globale des écarts sur la période sélectionnée."
            action={<StatusBadge label="Suivi rapproché" tone="warning" />}
          >
            <MockTrendChart points={varianceTrend} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard
            title="Alertes de risque"
            subtitle="Situations nécessitant analyse ou arbitrage."
          >
            <AlertList items={riskAlerts} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard
            title="Top variances négatives"
            subtitle="Postes dépassant la trajectoire budgétaire."
          >
            <MockBarChart data={topNegativeVariance} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard
            title="Top variances positives"
            subtitle="Postes en sous-consommation par rapport aux attentes."
          >
            <MockBarChart data={topPositiveVariance} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <SectionCard
            title="Causes probables"
            subtitle="Lecture simplifiée des causes dominantes d’écart."
            action={<StatusBadge label="Root cause" tone="info" />}
          >
            <MockBarChart data={rootCauseMix} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <SectionCard
            title="Lignes budgétaires à risque"
            subtitle="Lignes présentant les profils de dérive les plus marqués."
          >
            <MockBarChart data={lineItemsAtRisk} />
          </SectionCard>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <SectionCard
            title="Lecture de management"
            subtitle="Interprétation synthétique des écarts observés."
          >
            <Stack spacing={1.25}>
              {[
                ["Les achats hydrocarbures dominent le risque", "La pression budgétaire reste fortement concentrée sur les lignes d’approvisionnement énergétique."],
                ["Les investissements en retard faussent la lecture globale", "Une partie des variances positives est liée à des glissements d’exécution plutôt qu’à une économie réelle."],
                ["La qualification des causes améliore la gouvernance", "L’identification des causes racines permet de distinguer les écarts d’exécution, de prix ou de planification."],
              ].map(([title, desc]) => (
                <Box
                  key={title}
                  sx={{
                    p: 1.5,
                    borderRadius: "10px",
                    border: "1px solid #E2E8EE",
                    bgcolor: "#FFFFFF",
                  }}
                >
                  <Typography sx={{ fontSize: 13, fontWeight: 800, color: "#17324D" }}>
                    {title}
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: "#66788A", mt: 0.5, lineHeight: 1.65 }}>
                    {desc}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </SectionCard>
        </Grid>
      </Grid> */}
    </Box>
  );
}
}